import { supabase } from '../lib/supabase';
import { getChatCompletion } from './aiIntegrations/chatCompletion';

// Fix: Use correct Lambda-based AI availability check
const isOpenAIAvailable = () => {
  return !!import.meta.env?.VITE_AWS_LAMBDA_CHAT_COMPLETION_URL;
};

// Fix: Use Lambda-based AI for content generation
const generateOutreachContent = async (targetInfo, type, prompt) => {
  try {
    if (!isOpenAIAvailable()) {
      return {
        body: `Professional ${type} template for ${targetInfo?.industry || 'your industry'}. ${prompt}`,
        subject: `${type?.charAt(0)?.toUpperCase() + type?.slice(1)} - ${targetInfo?.company || 'Your Company'}`,
        personalization_elements: ['{{firstName}}', '{{companyName}}', '{{industry}}'],
        estimated_effectiveness: 'Medium',
        tone: 'professional'
      };
    }

    const response = await getChatCompletion(
      'OPEN_AI',
      'gpt-4o',
      [
        {
          role: 'system',
          content: 'You are an expert copywriter specializing in sales and marketing outreach. Generate compelling, personalized content. Always respond with valid JSON.'
        },
        {
          role: 'user',
          content: `Generate a ${type} template for: ${JSON.stringify(targetInfo)}\n\nContext: ${prompt}\n\nRespond with JSON: { body: string, subject: string, personalization_elements: string[], estimated_effectiveness: string, tone: string }`
        }
      ],
      { max_completion_tokens: 1500 }
    );

    const content = response?.choices?.[0]?.message?.content || '{}';
    try {
      return JSON.parse(content);
    } catch {
      return {
        body: content,
        subject: `${type} Template`,
        personalization_elements: ['{{firstName}}', '{{companyName}}'],
        estimated_effectiveness: 'Medium',
        tone: 'professional'
      };
    }
  } catch (error) {
    console.error('Error generating outreach content:', error);
    return {
      body: `Professional ${type} template. ${prompt}`,
      subject: `${type?.charAt(0)?.toUpperCase() + type?.slice(1)} Template`,
      personalization_elements: ['{{firstName}}', '{{companyName}}'],
      estimated_effectiveness: 'Medium',
      tone: 'professional'
    };
  }
};

class TemplateService {
  // Get templates with error handling for missing columns
  async getTemplates(filters = {}) {
    try {
      let query = supabase?.from('communication_templates')?.select(`
          *,
          template_analytics (
            views_count,
            usage_count,
            success_rate,
            response_rate,
            last_used_at
          )
        `)?.eq('is_active', true);

      // Apply filters with optional chaining
      if (filters?.category) {
        query = query?.eq('category', filters?.category);
      }

      if (filters?.type) {
        query = query?.eq('type', filters?.type);
      }

      if (filters?.aiGenerated !== undefined) {
        query = query?.eq('ai_generated', filters?.aiGenerated);
      }

      if (filters?.tags?.length > 0) {
        query = query?.overlaps('tags', filters?.tags);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) {
        console.warn('Template fetch error (may be schema issue):', error?.message);
        // Fallback to basic query if relationship fails
        const { data: basicData, error: basicError } = await supabase?.from('communication_templates')?.select('*')?.eq('is_active', true)?.order('created_at', { ascending: false });
        
        if (basicError) {
          throw basicError;
        }
        
        return basicData || [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching templates:', error);
      // Return empty array to prevent UI crashes
      return [];
    }
  }

  // Get template categories with fallback
  async getCategories() {
    try {
      const { data, error } = await supabase?.from('communication_templates')?.select('category')?.not('category', 'is', null);

      if (error) {
        console.warn('Category fetch error:', error?.message);
        return ['general', 'email_marketing', 'sms_marketing', 'onboarding', 'followup'];
      }

      const categories = [...new Set(data?.map(item => item?.category).filter(Boolean))] || [];
      return categories?.length > 0 ? categories : ['general'];
    } catch (error) {
      console.error('Error fetching categories:', error);
      return ['general'];
    }
  }

  // Get template categories with count and proper formatting for UI
  async getTemplateCategories() {
    try {
      const { data, error } = await supabase?.from('communication_templates')
        ?.select('category')
        ?.not('category', 'is', null)
        ?.eq('is_active', true);

      if (error) {
        console.warn('Category fetch error:', error?.message);
        return this.getDefaultCategories();
      }

      if (!data || data?.length === 0) {
        return this.getDefaultCategories();
      }

      // Count categories and format for UI
      const categoryCount = {};
      data?.forEach(item => {
        if (item?.category) {
          categoryCount[item?.category] = (categoryCount?.[item?.category] || 0) + 1;
        }
      });

      // Convert to UI format with counts
      const categories = [
        { key: 'all', label: 'All Categories', count: data?.length || 0 }
      ];

      Object.entries(categoryCount)?.forEach(([key, count]) => {
        categories?.push({
          key,
          label: this.formatCategoryLabel(key),
          count
        });
      });

      return categories?.length > 1 ? categories : this.getDefaultCategories();
    } catch (error) {
      console.error('Error fetching template categories:', error);
      return this.getDefaultCategories();
    }
  }

  // Helper method to get default categories
  getDefaultCategories() {
    return [
      { key: 'all', label: 'All Categories', count: 0 },
      { key: 'general', label: 'General', count: 0 },
      { key: 'email_marketing', label: 'Email Marketing', count: 0 },
      { key: 'sms_marketing', label: 'SMS Marketing', count: 0 },
      { key: 'onboarding', label: 'Onboarding', count: 0 },
      { key: 'followup', label: 'Follow-up', count: 0 }
    ];
  }

  // Helper method to format category labels
  formatCategoryLabel(category) {
    const labelMap = {
      general: 'General',
      email_marketing: 'Email Marketing',
      sms_marketing: 'SMS Marketing',
      onboarding: 'Onboarding',
      followup: 'Follow-up',
      lead_nurturing: 'Lead Nurturing',
      sales_outreach: 'Sales Outreach',
      customer_support: 'Customer Support',
      social_media: 'Social Media'
    };

    return labelMap?.[category] || category?.split('_')?.map(word => 
      word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ');
  }

  // Get template analytics with error handling
  async getTemplateAnalytics(templateId) {
    try {
      const { data, error } = await supabase?.from('template_analytics')?.select('*')?.eq('template_id', templateId)?.single();

      if (error && error?.code !== 'PGRST116') {
        console.warn('Analytics fetch error:', error?.message);
        return {
          views_count: 0,
          usage_count: 0,
          success_rate: 0.0,
          response_rate: 0.0
        };
      }

      return data || {
        views_count: 0,
        usage_count: 0,
        success_rate: 0.0,
        response_rate: 0.0
      };
    } catch (error) {
      console.error('Error fetching template analytics:', error);
      return {
        views_count: 0,
        usage_count: 0,
        success_rate: 0.0,
        response_rate: 0.0
      };
    }
  }

  // Create template with new fields
  async createTemplate(templateData) {
    try {
      const { data, error } = await supabase?.from('communication_templates')?.insert([{
          name: templateData?.name,
          type: templateData?.type,
          content: templateData?.content,
          subject: templateData?.subject,
          category: templateData?.category || 'general',
          tags: templateData?.tags || [],
          ai_generated: templateData?.ai_generated || false,
          performance_score: templateData?.performance_score || 0.0,
          user_id: templateData?.user_id
        }])?.select()?.single();

      if (error) throw error;

      // Create initial analytics record
      if (data?.id) {
        await this.createInitialAnalytics(data?.id, templateData?.user_id);
        await this.createInitialVersion(data, templateData?.user_id);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating template:', error);
      return { data: null, error };
    }
  }

  // Create initial analytics record
  async createInitialAnalytics(templateId, userId) {
    try {
      const { error } = await supabase?.from('template_analytics')?.insert([{
          template_id: templateId,
          user_id: userId,
          views_count: 0,
          usage_count: 0,
          success_rate: 0.0,
          response_rate: 0.0
        }]);

      if (error) {
        console.warn('Could not create analytics record:', error?.message);
      }
    } catch (error) {
      console.warn('Error creating initial analytics:', error);
    }
  }

  // Create initial version record
  async createInitialVersion(templateData, userId) {
    try {
      const { error } = await supabase?.from('template_versions')?.insert([{
          template_id: templateData?.id,
          user_id: userId,
          version_number: 1,
          content: templateData?.content,
          subject: templateData?.subject,
          change_notes: 'Initial version'
        }]);

      if (error) {
        console.warn('Could not create version record:', error?.message);
      }
    } catch (error) {
      console.warn('Error creating initial version:', error);
    }
  }

  // Track template usage
  async trackTemplateUsage(templateId, userId) {
    try {
      if (userId) {
        const { error } = await supabase?.from('template_analytics')?.upsert({
            template_id: templateId,
            user_id: userId,
            last_used_at: new Date()?.toISOString()
          }, {
            onConflict: 'template_id,user_id'
          });

        if (error) {
          console.warn('Could not track template usage:', error?.message);
        }
      } else {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user) return;

        const { error } = await supabase?.rpc('update_template_analytics', {
          template_uuid: templateId,
          user_uuid: user?.id
        });

        if (error) throw error;
      }
    } catch (error) {
      console.error('Error tracking template usage:', error);
    }
  }

  // Update an existing template
  async updateTemplate(templateId, updates) {
    try {
      const { data, error } = await supabase?.from('communication_templates')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', templateId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating template:', error);
      throw error;
    }
  }

  // Delete a template
  async deleteTemplate(templateId) {
    try {
      const { error } = await supabase?.from('communication_templates')?.delete()?.eq('id', templateId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  }

  // Generate AI-powered template
  async generateAITemplate(params) {
    try {
      const result = await generateOutreachContent(
        {
          industry: params?.industry || 'General',
          company: params?.companyName || 'target company',
          role: params?.targetRole || 'decision maker',
          painPoints: params?.painPoints || [],
          goals: params?.businessGoals || []
        },
        params?.type || 'email',
        params?.prompt || `Generate a professional ${params?.type || 'email'} template`
      );

      return { data: result, error: null };
    } catch (error) {
      console.error('Error generating AI template:', error);
      return { data: null, error };
    }
  }

  // Extract variables from template content
  extractVariables(content) {
    const variables = {};
    const matches = content?.match(/\{\{(\w+)\}\}/g) || [];
    
    matches?.forEach(match => {
      const variableName = match?.replace(/\{\{|\}\}/g, '');
      variables[variableName] = 'string';
    });

    return variables;
  }

  // Get template performance analytics
  async getTemplatePerformance(templateId) {
    try {
      const { data, error } = await supabase?.from('template_analytics')?.select('*')?.eq('template_id', templateId)?.single();

      if (error && error?.code !== 'PGRST116') throw error;
      return data || { usage_count: 0, performance_data: {} };
    } catch (error) {
      console.error('Error fetching template performance:', error);
      return { usage_count: 0, performance_data: {} };
    }
  }

  // Get template versions
  async getTemplateVersions(templateId) {
    try {
      const { data, error } = await supabase?.from('template_versions')?.select(`
          *,
          created_by_profile:user_profiles!created_by(first_name, last_name, email)
        `)?.eq('template_id', templateId)?.order('version_number', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching template versions:', error);
      return [];
    }
  }

  // Create template version
  async createTemplateVersion(templateId, versionData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase?.rpc('create_template_version', {
        template_uuid: templateId,
        version_content: versionData?.content,
        version_subject: versionData?.subject,
        change_desc: versionData?.changeDescription,
        creator_id: user?.id
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating template version:', error);
      throw error;
    }
  }

  // Get generation history for user
  async getGenerationHistory(limit = 20) {
    try {
      const { data, error } = await supabase?.from('template_generations')?.select(`
          *,
          template:communication_templates(name, type, category)
        `)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching generation history:', error);
      return [];
    }
  }

  // Duplicate template
  async duplicateTemplate(templateId, newName) {
    try {
      const { data: originalTemplate, error: fetchError } = await supabase?.from('communication_templates')?.select('*')?.eq('id', templateId)?.single();

      if (fetchError) throw fetchError;

      const duplicateData = {
        ...originalTemplate,
        id: undefined,
        name: newName || `${originalTemplate?.name} (Copy)`,
        ai_generated: false,
        ai_prompt: null,
        parent_template_id: templateId,
        version_number: 1,
        created_at: undefined,
        updated_at: undefined
      };

      return await this.createTemplate(duplicateData);
    } catch (error) {
      console.error('Error duplicating template:', error);
      throw error;
    }
  }

  // Get popular templates (public templates with high usage)
  async getPopularTemplates(limit = 10) {
    try {
      const { data, error } = await supabase?.from('communication_templates')?.select(`
          *,
          template_analytics!inner(usage_count, performance_data)
        `)?.gte('template_analytics.usage_count', 5)?.order('template_analytics.usage_count', { ascending: false })?.limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching popular templates:', error);
      return [];
    }
  }
}

export const templateService = new TemplateService();
export { generateOutreachContent, isOpenAIAvailable };
export default templateService;